import { DownOutlined, RightOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import styled from 'styled-components'

const Tree = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.55;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  flex-shrink: 0;
`

const Key = styled.span`
  color: #1677ff;
`

const Preview = styled.span`
  color: rgba(0, 0, 0, 0.45);
`

const Children = styled.div`
  margin-left: 16px;
`

const Primitive = styled.span<{
  $kind: 'string' | 'number' | 'boolean' | 'null'
}>`
  color: ${(props) => {
    if (props.$kind === 'string') {
      return '#c41d7f'
    }
    if (props.$kind === 'number') {
      return '#d4380d'
    }
    if (props.$kind === 'boolean') {
      return '#08979c'
    }
    return 'rgba(0, 0, 0, 0.45)'
  }};
`

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function collectionPreview(value: unknown): string {
  if (Array.isArray(value)) {
    return `Array(${value.length})`
  }
  if (isRecord(value)) {
    const keys = Object.keys(value)
    if (keys.length === 0) {
      return '{}'
    }
    const shown = keys.slice(0, 3).join(', ')
    return keys.length > 3 ? `{ ${shown}, … }` : `{ ${shown} }`
  }
  return ''
}

function PrimitiveValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <Primitive $kind="null">null</Primitive>
  }
  if (typeof value === 'string') {
    return <Primitive $kind="string">"{value}"</Primitive>
  }
  if (typeof value === 'number') {
    return <Primitive $kind="number">{String(value)}</Primitive>
  }
  if (typeof value === 'boolean') {
    return <Primitive $kind="boolean">{String(value)}</Primitive>
  }
  return <span>{String(value)}</span>
}

function Node({
  name,
  value,
  defaultOpen,
}: {
  name?: string
  value: unknown
  defaultOpen: boolean
}) {
  const isArray = Array.isArray(value)
  const isObject = isRecord(value)
  const canToggle = isArray || isObject
  const [open, setOpen] = useState(defaultOpen)

  if (!canToggle) {
    return (
      <Row>
        {name != null ? (
          <>
            <Key>{name}</Key>
            <span>: </span>
          </>
        ) : null}
        <PrimitiveValue value={value} />
      </Row>
    )
  }

  const entries = isArray
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value)
  const label = isArray ? 'array' : 'object'
  const toggleLabel = open ? `Collapse ${label}` : `Expand ${label}`

  return (
    <div>
      <Row>
        <Toggle
          type="button"
          aria-label={name == null ? toggleLabel : `${toggleLabel} ${name}`}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? (
            <DownOutlined aria-hidden="true" />
          ) : (
            <RightOutlined aria-hidden="true" />
          )}
        </Toggle>
        {name != null ? (
          <>
            <Key>{name}</Key>
            <span>: </span>
          </>
        ) : null}
        {open ? null : <Preview>{collectionPreview(value)}</Preview>}
      </Row>
      {open ? (
        <Children>
          {entries.length === 0 ? (
            <Preview>{isArray ? '[]' : '{}'}</Preview>
          ) : (
            entries.map(([key, child]) => (
              <Node key={key} name={key} value={child} defaultOpen={false} />
            ))
          )}
        </Children>
      ) : null}
    </div>
  )
}

export function CollapsibleJson({ value }: { value: unknown }) {
  return (
    <Tree>
      <Node value={value} defaultOpen />
    </Tree>
  )
}
